<template>
  <div>
    <form @submit.prevent="handleSubmit(!v$.$invalid)">
      <div class="formgrid grid">
        <div class="field col-12">
          <label
            :class="{
              'p-error': v$.form.account.$invalid && submitted,
            }"
          >
            Perkiraan
          </label>
          <AutoComplete
            autofocus
            ref="account"
            v-model="form.account"
            :suggestions="acAccount"
            :min-length="2"
            :class="{
              'p-invalid': v$.form.account.$invalid && submitted,
            }"
            class="w-full"
            input-class="w-full"
            field="nama"
            @complete="searchAccount($event)"
          >
            <template #item="slotProps">
              <div>{{ slotProps.item.kode }} - {{ slotProps.item.nama }}</div>
            </template>
          </AutoComplete>
          <small
            v-if="
              (v$.form.account.$invalid && submitted) ||
              v$.form.account.$pending.$response
            "
            class="p-error"
            >{{ v$.form.account.required.$message }}</small
          >
        </div>
        <div class="field col-12">
          <label
            :class="{
              'p-error': v$.form.keterangan.$invalid && submitted,
            }"
          >
            Keterangan</label
          >
          <InputText
            v-model="form.keterangan"
            type="text"
            :class="{
              'p-invalid': v$.form.keterangan.$invalid && submitted,
            }"
            class="w-full"
          />
          <small
            v-if="
              (v$.form.keterangan.$invalid && submitted) ||
              v$.form.keterangan.$pending.$response
            "
            class="p-error"
            >{{ v$.form.keterangan.required.$message }}</small
          >
        </div>
        <div class="field col-12">
          <label
            :class="{
              'p-error': v$.form.debit.$invalid && submitted,
            }"
          >
            Debit
          </label>
          <InputNumber
            v-model="form.debit"
            :minFractionDigits="0"
            :maxFractionDigits="2"
            :class="{
              'p-invalid': v$.form.debit.$invalid && submitted,
            }"
            mode="currency"
            currency="IDR"
            locale="id-ID"
            class="w-full"
            input-class="w-full"
            :disabled="this.form.kredit > 0"
            @keydown="onNumpadDecimal"
          />
          <small
            v-if="
              (v$.form.debit.$invalid && submitted) ||
              v$.form.debit.$pending.$response
            "
            class="p-error"
            >{{ v$.form.debit.required.$message }}</small
          >
        </div>
        <div class="field col-12">
          <label
            :class="{
              'p-error': v$.form.kredit.$invalid && submitted,
            }"
          >
            Kredit
          </label>
          <InputNumber
            v-model="form.kredit"
            :minFractionDigits="0"
            :maxFractionDigits="2"
            :class="{
              'p-invalid': v$.form.kredit.$invalid && submitted,
            }"
            mode="currency"
            currency="IDR"
            locale="id-ID"
            class="w-full"
            input-class="w-full"
            :disabled="this.form.debit > 0"
            @keydown="onNumpadDecimal"
          />
          <small
            v-if="
              (v$.form.kredit.$invalid && submitted) ||
              v$.form.kredit.$pending.$response
            "
            class="p-error"
            >{{ v$.form.kredit.required.$message }}</small
          >
        </div>
      </div>
      <div class="flex justify-content-end align-items-end">
        <Button
          label="Tutup"
          icon="pi pi-times"
          @click="onCloseForm"
          class="p-button-outlined mr-2"
        />
        <Button label="Simpan" icon="pi pi-check" type="submit" />
      </div>
    </form>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import errorHandler from '@/helpers/error-handler'
import AccountService from '@/services/AccountService'

export default {
  setup: () => ({ v$: useVuelidate() }),
  props: {
    id: {
      type: Number,
      default: 0,
    },
    item: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      submitted: false,
      accountService: null,
      acAccount: [],
      form: this.item,
    }
  },
  created() {
    this.accountService = new AccountService()
  },
  methods: {
    searchAccount(e) {
      this.accountService
        .get('?filter[nama]=' + e.query)
        .then((res) => {
          this.acAccount = res.data.data
        })
        .catch((err) => {
          errorHandler(err, 'Data Perkiraan', this)
        })
    },
    onCloseForm() {
      this.$emit('close')
    },
    handleSubmit(isFormValid) {
      this.submitted = true

      if (!isFormValid) {
        return
      }

      this.$emit('save', this.form)
      this.reset()
    },
    reset() {
      this.submitted = false
      this.form = {
        account: null,
        keterangan: '',
        debit: 0,
        kredit: 0,
      }
      this.$refs.account.$el.children[0].focus()
    },
    onNumpadDecimal(e) {
      if (!(e.key === '.' || e.key === ',' || e.code === 'NumpadDecimal')) return

      let input = e.target
      if (!input) return
      if (input.tagName !== 'INPUT') input = input.querySelector && input.querySelector('input')
      if (!input || input.tagName !== 'INPUT') return

      e.preventDefault()

      try {
        const raw = input.value ?? ''

        const prefixMatch = raw.match(/^[^\d\-]*/u)
        const prefix = prefixMatch ? prefixMatch[0] : ''

        const numeric = raw.slice(prefix.length)

        const startGlobal = input.selectionStart ?? raw.length
        const endGlobal = input.selectionEnd ?? startGlobal
        const start = Math.max(0, startGlobal - prefix.length)
        const end = Math.max(0, endGlobal - prefix.length)

        const inserted = numeric.slice(0, start) + ',' + numeric.slice(end)

        const cleaned = inserted.replace(/\./g, '').replace(/\s/g, '')
        const parts = cleaned.split(',')
        let intPart = (parts[0] || '0').replace(/[^\d\-]/g, '')
        let fracPart = (parts[1] || '').replace(/[^\d]/g, '')
        const isNegative = intPart.startsWith('-')
        intPart = intPart.replace('-', '') || '0'

        if (fracPart.length === 0) {
          const formattedInt = new Intl.NumberFormat('id-ID').format(parseInt(intPart, 10) || 0)
          const formattedNumeric = `${isNegative ? '-' : ''}${formattedInt},`
          input.value = prefix + formattedNumeric

          const caretPos = prefix.length + formattedNumeric.indexOf(',') + 1
          try { input.setSelectionRange(caretPos, caretPos) } catch (_) {}
        } else {
          const num = parseFloat(intPart + '.' + fracPart) || 0
          const rounded = Math.round(num * 100) / 100

          const absVal = Math.abs(rounded)
          const integer = Math.trunc(absVal)
          const frac = Math.round((absVal - integer) * 100)
          const fracStr = String(frac).padStart(2, '0')

          const formattedInt = new Intl.NumberFormat('id-ID').format(integer)
          const formattedNumeric = `${isNegative ? '-' : ''}${formattedInt},${fracStr}`

          input.value = prefix + formattedNumeric

          const caretPos = input.value.length
          try { input.setSelectionRange(caretPos, caretPos) } catch (_) {}
        }

        input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: ',' }))
        try { input.dispatchEvent(new Event('change', { bubbles: true })) } catch (_) {}
      } catch (err) {
        errorHandler(err, 'Input Decimal', this)
      }
    },
  },
  validations() {
    return {
      form: {
        account: {
          required: helpers.withMessage('Perkiraan harus diisi.', required),
        },
        keterangan: {
          required: helpers.withMessage('Keterangan harus diisi.', required),
        },
        debit: {
          required: helpers.withMessage('debit harus diisi.', required),
        },
        kredit: {
          required: helpers.withMessage('debit harus diisi.', required),
        },
      },
    }
  },
}
</script>
